import { useInfiniteQuery } from '@tanstack/react-query';
import type { IntercomContact, IntercomContactsResponse } from '@brand24-original/llm-types';
import { getIntercomContacts } from '../../services/admin/AdminIntercomService';
import { adminIntercomKeys } from '../../query/admin/keys';

export interface IntercomContactsFilters {
  query: string;
  tagIds: string[];
}

/**
 * Kontakty z Intercoma doładowywane w miejscu — kolejne strony dopisują się do listy, zamiast ją
 * podmieniać. Dzięki temu eksport obejmuje wszystko, co zdążyłeś dociągnąć, a nie jeden ekran.
 *
 * Endpoint stronicuje kursorem (`starting_after`), czyli dokładnie tak, jak `useInfiniteQuery`
 * oczekuje: `nextCursor` z ostatniej strony staje się parametrem następnej.
 */
export const useGetIntercomContacts = ({ query, tagIds }: IntercomContactsFilters) => {
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, isError, refetch } =
    useInfiniteQuery<IntercomContactsResponse, Error, IntercomContactsResponse[], readonly unknown[], string | null>({
      queryKey: adminIntercomKeys.contacts(query, tagIds),
      queryFn: ({ pageParam }) =>
        getIntercomContacts({
          query: query || undefined,
          tagIds: tagIds.length > 0 ? tagIds : undefined,
          cursor: pageParam ?? undefined,
        }),
      initialPageParam: null,
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      select: (result) => result.pages,
      staleTime: 60_000,
    });

  const pages = data ?? [];
  const contacts: IntercomContact[] = pages.flatMap((page) => page.contacts);

  return {
    contacts,
    // Dopóki trwa pierwsze pobranie, nie twierdzimy, że dostępu nie ma — inaczej widok mrugałby
    // komunikatem o braku połączenia.
    isAvailable: pages[0]?.available ?? true,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  };
};
